import React, { useState } from 'react';
import { Building2, Scale, Shield, Atom, Zap, ChevronRight, Award, Calendar, CheckCircle, FileText, Users, MapPin, ExternalLink, Hammer } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import LeadCaptureModal from '../LeadCaptureModal';

const CaseStudiesSection = () => {
  const [selectedCase, setSelectedCase] = useState(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  const cases = [
    {
      id: 1,
      client: "SENAI/DF",
      title: "Implantação e Implementação BIM",
      icon: Building2,
      color: "from-blue-600 to-blue-800",
      badgeColor: "bg-blue-600",
      period: "2022-2023",
      sector: "Educação Profissional",
      description: "Transformação digital completa do setor de projetos do SENAI/DF, desde o diagnóstico até a entrega de ambiente BIM funcional.",
      phases: [
        {
          name: "Implantação",
          items: ["Levantamento de informações", "Diagnóstico de maturidade BIM", "Planejamento estratégico"]
        },
        {
          name: "Implementação",
          items: ["BIM Mandate institucional", "Plano de Execução BIM (BEP)", "Bibliotecas BIM personalizadas", "Template de trabalho customizado"]
        }
      ],
      pilotProject: {
        title: "Edificação SENAI Taguatinga",
        description: "Levantamento as-built da edificação existente e atualização completa dos projetos de arquitetura e instalações."
      },
      highlights: ["Diagnóstico completo", "Ambiente BIM funcional", "Documentação normativa"],
      hasAttestation: false,
      image: "https://images.unsplash.com/photo-1636142426623-98a21fe2fb8f?w=800"
    },
    {
      id: 2,
      client: "STM",
      fullName: "Superior Tribunal Militar",
      title: "Transformação Digital BIM no Poder Judiciário",
      icon: Scale,
      color: "from-purple-600 to-purple-800",
      badgeColor: "bg-purple-600",
      period: "2018-2020",
      sector: "Poder Judiciário",
      description: "Primeiro órgão do Poder Judiciário brasileiro a implementar BIM, com capacitação completa da equipe técnica e desenvolvimento de ambiente digital personalizado.",
      phases: [
        {
          name: "Capacitação",
          items: ["Curso Master em Revit (160 horas)", "Curso de Navisworks (16 horas)", "Tutoria de projeto piloto (48 horas)"]
        },
        {
          name: "Implementação",
          items: ["BIM Mandate institucional", "Template federado multidisciplinar", "Biblioteca com 100 peças BIM (LOD 300)"]
        }
      ],
      pilotProject: {
        title: "Projetos do STM",
        description: "Desenvolvimento de projeto piloto utilizando projetos existentes de arquitetura e engenharia do tribunal."
      },
      highlights: ["160h de treinamento", "100 peças BIM", "Contrato estendido 2x"],
      hasAttestation: true,
      attestationInfo: "CAT - Certidão de Atestado Técnico emitida pelo STM",
      image: "https://images.unsplash.com/photo-1636142466028-8c6c7cd616b7?w=800"
    },
    {
      id: 3,
      client: "Exército Brasileiro",
      fullName: "Departamento de Obras Militares",
      title: "Padronização BIM Nacional para Obras Militares",
      icon: Shield,
      color: "from-green-600 to-green-800",
      badgeColor: "bg-green-600",
      period: "2016",
      sector: "Defesa Nacional",
      description: "Criação do padrão nacional de BIM para todas as obras militares do Brasil, com template único distribuído para todas as regiões.",
      phases: [
        {
          name: "Implementação",
          items: ["Template Norma DOM", "Bibliotecas BIM militares", "Treinamento avançado em Revit"]
        }
      ],
      pilotProject: null,
      highlights: ["Padrão nacional", "Todas as CROs do Brasil", "Pioneiro em 2016"],
      leadership: "Coronel Washington Luke",
      impact: {
        title: "Impacto Nacional",
        description: "Template distribuído para todas as Comissões Regionais de Obras (CROs) em todo o território brasileiro, padronizando projetos militares."
      },
      hasAttestation: false,
      image: "https://images.unsplash.com/photo-1731481903241-9603714a9f05?w=800"
    },
    {
      id: 4,
      client: "Amazul",
      fullName: "Amazônia Azul Tecnologias de Defesa",
      title: "Implementação BIM em Tecnologia de Defesa",
      icon: Atom,
      color: "from-cyan-600 to-cyan-800",
      badgeColor: "bg-cyan-600",
      period: "2024-2025",
      sector: "Defesa e Tecnologia",
      description: "Implementação BIM em empresa estratégica do setor de defesa nacional, criando autonomia para a equipe interna desenvolver projetos complexos.",
      phases: [
        {
          name: "Implantação",
          items: ["Levantamento de informações", "Diagnóstico de maturidade BIM", "Planejamento estratégico"]
        },
        {
          name: "Implementação",
          items: ["BIM Mandate institucional", "Plano de Execução BIM (BEP)"]
        }
      ],
      pilotProject: {
        title: "Nova Edificação Amazul",
        description: "Projeto novo que será executado e construído através dos projetos de arquitetura e instalações desenvolvidos pela equipe Amazul."
      },
      highlights: ["Projeto atual", "Será construído", "Setor estratégico"],
      hasAttestation: false,
      image: "https://images.unsplash.com/photo-1636142426623-98a21fe2fb8f?w=800"
    },
    {
      id: 5,
      client: "CEMIG GT",
      fullName: "Companhia Energética de Minas Gerais",
      title: "BIM para Infraestrutura Energética",
      icon: Zap,
      color: "from-yellow-600 to-orange-600",
      badgeColor: "bg-yellow-600",
      period: "2020 e 2025-2026",
      sector: "Energia",
      description: "Parceria de longa data para implementação BIM no setor de geração e transmissão de energia, com foco em subestações, barragens e usinas.",
      phases: [
        {
          name: "Primeiro Contrato (2020)",
          items: ["Diagnóstico inicial", "BIM Mandate", "Plano de Execução BIM"]
        },
        {
          name: "Atualização (2025-2026)",
          items: ["Atualização para nova equipe", "Revisão do BIM Mandate", "Novo planejamento estratégico"]
        }
      ],
      pilotProject: null,
      areaOfWork: [
        { icon: Zap, label: "Subestações de energia" },
        { icon: "🌊", label: "Barragens" },
        { icon: "🏭", label: "Usinas de energia elétrica" }
      ],
      highlights: ["Parceria desde 2020", "Infraestrutura crítica", "Energia de MG"],
      hasAttestation: false,
      image: "https://images.unsplash.com/photo-1760015417760-c14d2fcd0853?w=800"
    },
    {
      id: 6,
      client: "Construtora ADN",
      fullName: "ADN Construtora e Incorporadora",
      title: "Transformação BIM em Construtora de Grande Porte",
      icon: Hammer,
      color: "from-orange-600 to-red-700",
      badgeColor: "bg-orange-600",
      period: "2022-2023",
      sector: "Construção Civil",
      description: "Implementação BIM completa em construtora de grande porte de São Paulo, incluindo palestra presencial, diagnóstico baseado em PMBOK e capacitação intensiva de mais de 170 horas.",
      phases: [
        {
          name: "Implantação",
          items: [
            "Visita presencial à sede em SP",
            "Palestra BIM para equipe de projetos",
            "Questionário baseado no PMBOK",
            "Diagnóstico de maturidade",
            "Reuniões com fornecedores de tecnologia",
            "Planejamento estratégico"
          ]
        },
        {
          name: "Implementação",
          items: [
            "BIM Mandate institucional",
            "Plano de Execução BIM (BEP)",
            "Bibliotecas BIM personalizadas",
            "Template de trabalho customizado",
            "Curso de Revit (170+ horas)"
          ]
        }
      ],
      pilotProject: {
        title: "Edifício Residencial Real",
        description: "Projeto piloto em edifício real a ser construído pela construtora, incluindo todos os projetos de arquitetura e instalações, com reuniões junto aos projetistas externos contratados."
      },
      training: {
        title: "Capacitação Intensiva",
        hours: "170+ horas",
        description: "Treinamento completo em Revit para as equipes de projeto"
      },
      highlights: ["170h de treinamento", "Palestra presencial", "Projeto real construído"],
      hasAttestation: false,
      image: "https://images.unsplash.com/photo-1669236392098-133cf2fe76af?w=800"
    }
  ];

  const CaseModal = ({ caseData, onClose }) => {
    if (!caseData) return null;
    const Icon = caseData.icon;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
        <div 
          className="bg-zinc-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-zinc-800"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Image */}
          <div className={`h-48 bg-gradient-to-br ${caseData.color} relative`}>
            <img 
              src={caseData.image} 
              alt={caseData.client}
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Icon className="w-20 h-20 text-white/80" />
            </div>
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
            >
              ✕
            </button>
          </div>

          <div className="p-6">
            {/* Title */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <Badge className={`${caseData.badgeColor} text-white mb-2`}>{caseData.sector}</Badge>
                <h2 className="text-2xl font-bold text-white">{caseData.client}</h2>
                {caseData.fullName && (
                  <p className="text-gray-400 text-sm">{caseData.fullName}</p>
                )}
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-gray-400">
                  <Calendar size={14} />
                  <span className="text-sm">{caseData.period}</span>
                </div>
              </div>
            </div>

            <h3 className="text-lg text-red-500 font-semibold mb-3">{caseData.title}</h3>
            <p className="text-gray-300 mb-6">{caseData.description}</p>

            {/* Phases */}
            <div className="space-y-4 mb-6">
              {caseData.phases.map((phase, idx) => (
                <div key={idx} className="bg-zinc-800 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full ${caseData.badgeColor} flex items-center justify-center text-xs`}>
                      {idx + 1}
                    </span>
                    {phase.name}
                  </h4>
                  <ul className="space-y-1">
                    {phase.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Pilot Project */}
            {caseData.pilotProject && (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-6">
                <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                  <MapPin size={16} />
                  Projeto Piloto: {caseData.pilotProject.title}
                </h4>
                <p className="text-gray-300 text-sm">{caseData.pilotProject.description}</p>
              </div>
            )}

            {/* Area of Work (CEMIG) */}
            {caseData.areaOfWork && (
              <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 mb-6">
                <h4 className="text-yellow-400 font-semibold mb-3">Áreas de Atuação</h4>
                <div className="flex flex-wrap gap-3">
                  {caseData.areaOfWork.map((area, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-zinc-800 px-3 py-2 rounded-lg">
                      {typeof area.icon === 'string' ? (
                        <span>{area.icon}</span>
                      ) : (
                        <area.icon size={16} className="text-yellow-500" />
                      )}
                      <span className="text-gray-300 text-sm">{area.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Impact (Exército) */}
            {caseData.impact && (
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 mb-6">
                <h4 className="text-green-400 font-semibold mb-2 flex items-center gap-2">
                  <Users size={16} />
                  {caseData.impact.title}
                </h4>
                <p className="text-gray-300 text-sm">{caseData.impact.description}</p>
                {caseData.leadership && (
                  <p className="text-gray-400 text-xs mt-2">Liderança: {caseData.leadership}</p>
                )}
              </div>
            )}

            {/* Attestation */}
            {caseData.hasAttestation && (
              <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <Award className="text-purple-400" size={24} />
                  <div>
                    <h4 className="text-purple-400 font-semibold">Atestado de Capacidade Técnica</h4>
                    <p className="text-gray-400 text-sm">{caseData.attestationInfo}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Highlights */}
            <div className="flex flex-wrap gap-2">
              {caseData.highlights.map((h, idx) => (
                <Badge key={idx} variant="outline" className="border-zinc-600 text-gray-300">
                  {h}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="cases" className="py-20 bg-black" data-testid="case-studies-section">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-red-600/20 text-red-500 border-red-600 mb-4">
            Cases de Sucesso
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Consultoria BIM em Grandes Organizações
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Mais de 10 anos transformando a forma como grandes organizações públicas e privadas 
            projetam e constroem através da metodologia BIM.
          </p>
        </div>

        {/* Cases Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((caseItem) => {
            const Icon = caseItem.icon;
            return (
              <Card 
                key={caseItem.id}
                className={`bg-gradient-to-br ${caseItem.color} border-0 cursor-pointer group hover:scale-105 transition-all duration-300 overflow-hidden`}
                onClick={() => setSelectedCase(caseItem)}
              >
                <CardContent className="p-0">
                  <div className="relative h-40">
                    <img 
                      src={caseItem.image} 
                      alt={caseItem.client}
                      className="w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon className="w-16 h-16 text-white/60 group-hover:scale-110 transition-transform" />
                    </div>
                    {caseItem.hasAttestation && (
                      <div className="absolute top-3 right-3">
                        <Award className="w-6 h-6 text-yellow-400" />
                      </div>
                    )}
                  </div>
                  <div className="p-5 bg-zinc-900/80 backdrop-blur">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-white">{caseItem.client}</h3>
                      <span className="text-xs text-gray-400">{caseItem.period}</span>
                    </div>
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                      {caseItem.title}
                    </p>
                    <div className="flex items-center justify-between">
                      <Badge className={`${caseItem.badgeColor} text-white text-xs`}>
                        {caseItem.sector}
                      </Badge>
                      <span className="text-red-500 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Ver detalhes
                        <ChevronRight size={16} />
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-400 mb-4">
            Quer transformar sua organização com BIM?
          </p>
          <Button 
            onClick={() => setIsLeadModalOpen(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            Solicitar Consultoria
            <ChevronRight className="ml-2" size={18} />
          </Button>
        </div>
      </div>

      {/* Modal de Case */}
      {selectedCase && (
        <CaseModal caseData={selectedCase} onClose={() => setSelectedCase(null)} />
      )}

      {/* Lead Capture Modal */}
      <LeadCaptureModal 
        isOpen={isLeadModalOpen} 
        onClose={() => setIsLeadModalOpen(false)} 
        preSelectedService="Consultoria BIM"
      />
    </section>
  );
};

export default CaseStudiesSection;
